import React, { useState } from 'react';
import { Box, Typography, Button } from '@mui/material';

const ActivityCard = ({ image, title, description, buttonText, onClick, enabled = true, showOnAirRibbon = false }) => {
    const [isHovered, setIsHovered] = useState(false);
    const isDisabled = enabled === false;

    return (
        <Box
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={onClick}
            sx={{
                background: isDisabled
                    ? 'linear-gradient(135deg, rgba(30,30,40,0.8) 0%, rgba(40,40,50,0.8) 100%)'
                    : 'linear-gradient(135deg, rgba(20,0,40,0.6) 0%, rgba(40,0,60,0.6) 100%)',
                backdropFilter: 'blur(10px)',
                borderRadius: '24px',
                padding: { xs: '1.5rem', md: '2rem' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: isHovered && !isDisabled
                    ? '0 12px 40px rgba(255,0,255,0.3), 0 0 20px rgba(0,255,255,0.2), inset 0 0 30px rgba(255,255,255,0.03)'
                    : '0 6px 24px rgba(255,0,255,0.15), inset 0 0 20px rgba(255,255,255,0.02)',
                transform: isHovered && !isDisabled ? 'translateY(-6px) scale(1.01)' : 'translateY(0) scale(1)',
                position: 'relative',
                overflow: 'hidden',
                border: '2px solid transparent',
                opacity: isDisabled ? 0.7 : 1,
                filter: isDisabled ? 'grayscale(0.6)' : 'none',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: '24px',
                    padding: '2px',
                    background: isHovered && !isDisabled
                        ? 'linear-gradient(135deg, #ff00ff, #00ffff, #ff00ff)'
                        : 'linear-gradient(135deg, rgba(255,0,255,0.3), rgba(0,255,255,0.3))',
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                    transition: 'all 0.4s ease',
                    pointerEvents: 'none',
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'radial-gradient(circle at 50% 50%, rgba(255,0,255,0.1) 0%, transparent 70%)',
                    opacity: isHovered && !isDisabled ? 1 : 0,
                    transition: 'opacity 0.4s ease',
                    pointerEvents: 'none',
                }
            }}
        >
            {/* On Air ribbon */}
            {showOnAirRibbon && !isDisabled && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        zIndex: 2,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #e53935 0%, #ff5722 100%)',
                        boxShadow: '0 2px 8px rgba(229,57,53,0.5)',
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            color: '#fff',
                            fontWeight: 700,
                            letterSpacing: '0.5px',
                            textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                        }}
                    >
                        ON AIR
                    </Typography>
                </Box>
            )}
            {/* Icon/Image Container */}
            <Box
                sx={{
                    marginBottom: '1.5rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: { xs: '100px', md: '140px' },
                    position: 'relative',
                    zIndex: 1,
                }}
            >
                <Box
                    component="img"
                    src={image}
                    alt={title}
                    sx={{
                        maxWidth: '100%',
                        maxHeight: { xs: '120px', md: '160px' },
                        objectFit: 'contain',
                        filter: isHovered ? 'drop-shadow(0 4px 10px rgba(0,0,0,0.3))' : 'none',
                        transition: 'all 0.3s ease',
                        transform: isHovered ? 'scale(1.05)' : 'scale(1)',
                    }}
                />
            </Box>

            {/* Title */}
            <Typography
                variant="h6"
                sx={{
                    color: '#ffffff',
                    fontWeight: 800,
                    marginBottom: '1rem',
                    fontSize: { xs: '1.1rem', md: '1.3rem' },
                    position: 'relative',
                    zIndex: 1,
                    textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                    letterSpacing: '0.5px',
                }}
            >
                {title}
            </Typography>

            {/* Description */}
            <Typography
                variant="body2"
                sx={{
                    color: 'rgba(255,255,255,0.9)',
                    lineHeight: 1.6,
                    marginBottom: '1.5rem',
                    fontSize: { xs: '0.9rem', md: '1rem' },
                    flexGrow: 1,
                    textAlign: 'center',
                    position: 'relative',
                    zIndex: 1,
                    textShadow: '0 1px 5px rgba(0,0,0,0.5)',
                }}
            >
                {description}
            </Typography>

            {/* Button */}
            <Button
                variant="contained"
                sx={{
                    background: isHovered 
                        ? 'linear-gradient(135deg, #00ffff 0%, #ff00ff 100%)'
                        : 'linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)',
                    color: 'white',
                    borderRadius: '50px',
                    padding: { xs: '0.875rem 2rem', md: '1rem 2.5rem' },
                    fontSize: { xs: '0.95rem', md: '1.05rem' },
                    fontWeight: 700,
                    textTransform: 'none',
                    width: '100%',
                    boxShadow: isHovered 
                        ? '0 8px 24px rgba(255,0,255,0.4), 0 0 15px rgba(0,255,255,0.2)' 
                        : '0 4px 16px rgba(255,0,255,0.3)',
                    position: 'relative',
                    zIndex: 1,
                    border: '2px solid rgba(255,255,255,0.3)',
                    letterSpacing: '0.5px',
                    '&:hover': {
                        boxShadow: '0 10px 30px rgba(255,0,255,0.5), 0 0 20px rgba(0,255,255,0.3)',
                        transform: 'translateY(-2px) scale(1.02)',
                    },
                    transition: 'all 0.3s ease',
                }}
            >
                {buttonText}
            </Button>
        </Box>
    );
};

export default ActivityCard;
